import ConfigBusiness from '../config/ConfigBusiness';

// Unit type constants — keep in sync with UnitType.php enum
var UnitType = Object.freeze({
  DISTANCE:           'distance',
  DISTANCE_PLURAL:    'distancePlural',
  VOLUME:             'volume',
  VOLUME_FULL:        'volumeFull',
  WEIGHT:             'weight',
  DIMENSION:          'dimension',
  FUEL_EFFICIENCY:    'fuelEfficiency',
  COST_PER_DISTANCE:  'costPerDistance',
  DISTANCE_PER_VOLUME: 'distancePerVolume',
  COST_PER_VOLUME:    'costPerVolume',
});

// Conversion factors: metric (storage) -> imperial (display)
const FACTORS = {
  distance: 0.621371,   // km -> mi
  volume: 0.264172,     // liters -> gallons
  weight: 2.20462,      // kg -> lb
  dimension: 3.28084,   // meters -> feet
};

// react-intl message IDs per unit type and system
const MESSAGE_IDS = {
  metrico: {
    distance: 'units.metrico.distance',
    distancePlural: 'units.metrico.distancePlural',
    volume: 'units.metrico.volume',
    volumeFull: 'units.metrico.volumeFull',
    weight: 'units.metrico.weight',
    dimension: 'units.metrico.dimension',
    fuelEfficiency: 'units.metrico.fuelEfficiency',
    costPerDistance: 'units.metrico.costPerDistance',
    distancePerVolume: 'units.metrico.distancePerVolume',
    costPerVolume: 'units.metrico.costPerVolume',
  },
  imperial: {
    distance: 'units.imperial.distance',
    distancePlural: 'units.imperial.distancePlural',
    volume: 'units.imperial.volume',
    volumeFull: 'units.imperial.volumeFull',
    weight: 'units.imperial.weight',
    dimension: 'units.imperial.dimension',
    fuelEfficiency: 'units.imperial.fuelEfficiency',
    costPerDistance: 'units.imperial.costPerDistance',
    distancePerVolume: 'units.imperial.distancePerVolume',
    costPerVolume: 'units.imperial.costPerVolume',
  },
};

// Spanish defaults (used as fallback when translations aren't loaded)
const DEFAULTS = {
  metrico: {
    distance: 'km',
    distancePlural: 'kms',
    volume: 'lts',
    volumeFull: 'litros',
    weight: 'kg',
    dimension: 'mts',
    fuelEfficiency: 'LT/100Km',
    costPerDistance: 'Costo/Km',
    distancePerVolume: 'Kms por Litro',
    costPerVolume: 'Costo por Litro',
  },
  imperial: {
    distance: 'mi',
    distancePlural: 'mi',
    volume: 'gal',
    volumeFull: 'galones',
    weight: 'lb',
    dimension: 'ft',
    fuelEfficiency: 'GAL/100Mi',
    costPerDistance: 'Costo/Mi',
    distancePerVolume: 'Mi por Galón',
    costPerVolume: 'Costo por Galón',
  },
};

var _cachedSystem = null;

function getSystem() {
  if (_cachedSystem) return _cachedSystem;
  var value = ConfigBusiness.get('generales.sistemaUnidades');
  if (value === 'metrico' || value === 'imperial') {
    _cachedSystem = value;
    return value;
  }
  _cachedSystem = 'metrico';
  return 'metrico';
}

function refreshSystem() {
  _cachedSystem = null;
}

function isImperial() {
  return getSystem() === 'imperial';
}

/**
 * Returns the react-intl message descriptor { id, defaultMessage } for a unit label.
 * Use with <FormattedMessage {...UnitSystem.message('distance')} />
 * or intl.formatMessage(UnitSystem.message('distance'))
 */
function message(unitType) {
  var system = getSystem();
  var id = (MESSAGE_IDS[system] && MESSAGE_IDS[system][unitType]) || MESSAGE_IDS.metrico[unitType];
  var defaultMsg = (DEFAULTS[system] && DEFAULTS[system][unitType]) || DEFAULTS.metrico[unitType];
  if (!id) {
    return { id: 'units.unknown', defaultMessage: '' };
  }
  return { id: id, defaultMessage: defaultMsg };
}

/**
 * Converts a value from metric (storage) to the instance's display unit.
 * Returns the value unchanged if the instance is metric.
 */
function toDisplay(value, unitType) {
  if (value === null || value === undefined || value === '') return value;
  if (!isImperial()) return value;

  const num = parseFloat(value);
  if (isNaN(num)) return value;

  if (unitType === 'fuelEfficiency') {
    // LT/100Km -> GAL/100Mi: (liters * gal_factor) / (km * mi_factor)
    // Simplifies to: value * (FACTORS.volume / FACTORS.distance)
    return num * (FACTORS.volume / FACTORS.distance);
  }

  if (unitType === 'distancePerVolume') {
    // Km/LT -> Mi/Gal: (km * mi_factor) / (liters * gal_factor)
    return num * (FACTORS.distance / FACTORS.volume);
  }

  const factor = FACTORS[unitType];
  if (!factor) return value;
  return num * factor;
}

/**
 * Converts a value from the instance's display unit back to metric (storage).
 * Returns the value unchanged if the instance is metric.
 */
function toStorage(value, unitType) {
  if (value === null || value === undefined || value === '') return value;
  if (!isImperial()) return value;

  const num = parseFloat(value);
  if (isNaN(num)) return value;

  if (unitType === 'fuelEfficiency') {
    return num / (FACTORS.volume / FACTORS.distance);
  }

  if (unitType === 'distancePerVolume') {
    return num / (FACTORS.distance / FACTORS.volume);
  }

  const factor = FACTORS[unitType];
  if (!factor) return value;
  return num / factor;
}

/**
 * Formats a value for display: converts and rounds to specified decimals.
 */
function formatDisplay(value, unitType, decimals) {
  if (decimals === undefined) decimals = 2;
  var converted = toDisplay(value, unitType);
  var num = (typeof converted === 'number') ? converted : parseFloat(converted);
  if (isNaN(num)) return converted;
  return parseFloat(num.toFixed(decimals));
}

const UnitSystem = {
  getSystem,
  isImperial,
  refreshSystem,
  message,
  toDisplay,
  toStorage,
  formatDisplay,
  UnitType,
};

export { UnitType };
export default UnitSystem;
